import { SIGNATURE_BYTES } from './devices';
import { ownBytes } from './encoding';

const ECDSA = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const ECDH = { name: 'ECDH', namedCurve: 'P-256' } as const;

/** Imports a raw ECDSA P-256 public key. Throws when the bytes are not a point on the curve. */
export function importVerifyKey(raw: Uint8Array): Promise<CryptoKey> {
  return globalThis.crypto.subtle.importKey('raw', ownBytes(raw), ECDSA, true, ['verify']);
}

/** Imports a raw ECDH P-256 public key. Throws when the bytes are not a point on the curve. */
export function importEncryptionPublicKey(raw: Uint8Array): Promise<CryptoKey> {
  return globalThis.crypto.subtle.importKey('raw', ownBytes(raw), ECDH, true, []);
}

/** Signs with ECDSA P-256 and SHA-256, returning the 64-byte r and s form. */
export async function signBytes(privateKey: CryptoKey, data: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  const signature = await globalThis.crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, privateKey, ownBytes(data));
  return new Uint8Array(signature);
}

/** Checks an ECDSA P-256 SHA-256 signature. Returns false for a bad signature or an unusable key. */
export async function verifySignature(
  publicKeyRaw: Uint8Array,
  signature: Uint8Array,
  data: Uint8Array,
): Promise<boolean> {
  if (signature.length !== SIGNATURE_BYTES) return false;
  try {
    const key = await importVerifyKey(publicKeyRaw);
    return await globalThis.crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      key,
      ownBytes(signature),
      ownBytes(data),
    );
  } catch {
    return false;
  }
}
